import { Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from "@angular/core";
import { CameraChannelType } from "@app/main/components/videos/enums/CameraChannelType";
import { CameraLocation } from "@app/main/components/videos/enums/CameraLocation";
import { ICameraChannel } from "@app/main/components/videos/models/ICameraChannel";
import { ICameraEventPeriod } from "@app/main/components/videos/models/ICameraEventPeriod";
import { IMedia } from "@app/main/components/videos/models/IMedia";
import { IVideoAccelerometerData } from "@app/main/components/videos/models/IVideoAccelerometerData";
import { IVideoChannelMedia } from "@app/main/components/videos/models/IVideoChannelMedia";
import { IVideoEntry } from "@app/main/components/videos/models/IVideoEntry";
import { CameraService } from "@app/main/components/videos/services/camera.service";
import { VideoEntryService } from "@app/main/components/videos/services/video-entry.service";
import { CameraChannelService } from "@app/main/modules/cameras/_services/camera-channels.service";
import { ConstantsService } from "@app/services/constantsService";
import { DateService, DateUnit } from "@app/services/date.service";
import { FullScreenService } from "@app/services/fullscreen.service";
import { TranslationService } from "@app/services/translation.service";
import { animFadeInOut } from "@app/styles/animations/fade-in-fade-out.animation";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { IVideoSectionLoaded } from "../interfaces/IVideoSectionLoaded";
import { VVideoComponent } from "../v-video.component";
import './v-video-collection.component.scss';

@UntilDestroy()
@Component({
    selector: 'v-video-collection',
    template: require('./v-video-collection.component.html'),
    animations: [animFadeInOut]
})
export class VVideoCollectionComponent implements OnInit, OnDestroy, OnChanges {
    @Input() video: IVideoEntry;
    @Input() vehicleId: number;

    @Output() closeVideos = new EventEmitter<void>();
    @Output() secondsUpdate = new EventEmitter<number>();
    @Output() videoPlayStateChange = new EventEmitter<boolean>();

    @ViewChildren('videoChannel') vVideoComponents: QueryList<VVideoComponent>;
    //--

    fitToFrame = false;
    isZoomed = false;

    playbackSpeed = 1;
    showPlaybackSpeedOptions = false;
    vertical = false;
    fullscreen = false;
    ignore = false;

    firstPlayStarted: boolean = false;
    isPlaying = false;
    secondsPlayed: number;
    maxVideoDuration = 0;

    showLargeControls = false;
    mouseOverTimeout: any;
    showPlayStateIndicator: boolean;

    loadedVideoSections: IVideoSectionLoaded[] = [];
    activityPeriods: ICameraEventPeriod[];

    cameraChannels: ICameraChannel[];

    currentAccelerometerData: IVideoAccelerometerData;

    CameraLocation = CameraLocation;

    @HostBinding('class.is-mobile') isMobile: boolean;

    rafNumber: number;
    lastMedianPlayTime: number;
    animateCallCount = 0;
    actualAnimationCount = 0;

    constructor(
        private readonly el: ElementRef,
        private readonly cameraChannelService: CameraChannelService,
        private readonly dateService: DateService,
        private readonly fullScreenService: FullScreenService,
        private readonly constantsService: ConstantsService,
        private readonly translationService: TranslationService,
        private readonly cameraService: CameraService,
        private readonly videoEntryService: VideoEntryService) { }

    ngOnInit(): void {
        this.initialise();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["video"]) {
            this.getChannelNames();
            this.videoEntryService.getExtensionData(this.video).then(data => {
                if (!data) {
                    return;
                }

                this.activityPeriods = this.cameraService.getRegionsOfAccelerometerActivity(data);
            });
        }
    }

    ngAfterViewInit(): void {
        this.checkStartPlay();
    }

    ngOnDestroy(): void {
        this.stopAnimation();
    }

    closeVideo(): void {
        this.closeVideos.emit();
    }

    showFullscreen(): void {
        this.fullscreen = this.fullScreenService.toggle(this.el.nativeElement);
    }

    onPlayClicked(): void {
        this.play();
        // had to remove this call from original play method
        // since it's called by mediaLoaded method via checkStartPlay and thus
        // was triggering the appearance of play/pause buttons when no need to it
        this.temporarilyShowPlayStateIndicator();
    }

    onPauseClicked(): void {
        this.pause();
        this.temporarilyShowPlayStateIndicator();
    }

    stopPreviewClicked(): void {
        this.pause();
        this.emitIsPlaying(false);
    }

    mouseOverVideo(isHovering: boolean): void {
        if (this.mouseOverTimeout) {
            clearTimeout(this.mouseOverTimeout);
            this.mouseOverTimeout = null;
        }

        if (isHovering) {
            this.showLargeControls = true;
        }
        else {
            // We delay fade aways to make the UI feel nicer
            this.mouseOverTimeout = setTimeout(() => {
                this.showLargeControls = false || this.isMobile;
            }, 300);
        }
    }

    videoMouseMove(event: MouseEvent): void {

        // This gives you the position of the image on the page
        var bbox = (event.target as Element).getBoundingClientRect();

        // Then we measure how far into the image the mouse is in both x and y directions
        var mouseX = event.clientX - bbox.left;
        var mouseY = event.clientY - bbox.top;

        // Then work out how far through the image as a percentage the mouse is
        var xPercent = (mouseX / bbox.width) * 100;
        var yPercent = (mouseY / bbox.height) * 100;

        // Then we change the `transform-origin` css property on the image to center the zoom effect on the mouse position

        // It's a bit clearer in jQuery:
        $(event.target).css('transform-origin', (xPercent + '% ' + yPercent + '%'));
    }

    onSectionLoaded(loadedSections: IVideoSectionLoaded[]): void {
        this.loadedVideoSections = loadedSections;
    }

    focusChannel(channel: IVideoChannelMedia): void {
        let previousMainChannel: IVideoChannelMedia = null;
        this.video.channelMedia.forEach(c => {
            if (c.mainChannel) {
                previousMainChannel = c;
            }

            c.mainChannel = false;
        });

        channel.mainChannel = true;

        if (channel == previousMainChannel) {
            this.togglePlayPauseState();
        }
    }

    onSpeedChanged(speed): void {
        this.playbackSpeed = speed;
    }

    togglePlayPauseState(): void {
        if (this.isPlaying) {
            this.pause();
        }
        else {
            this.play();
        }
    }

    onFitToFrame(): void {
        this.fitToFrame = !this.fitToFrame;
    }

    onZoom(): void {
        this.isZoomed = !this.isZoomed;
    }

    temporarilyShowPlayStateIndicator(): void {
        this.showPlayStateIndicator = true;

        setTimeout(() => {
            this.showPlayStateIndicator = false;
        }, 300);
    }

    timeUpdate(secondsPlayed: number): boolean {
        if (!this.video) {
            return;
        }

        this.secondsPlayed = secondsPlayed;

        let currentVideoTime = this.video.VideoStartTime;

        if (this.video.VideoExtensionData?.VideoAccelerometerData?.length) {
            currentVideoTime = this.video.VideoExtensionData?.VideoAccelerometerData[0].Timestamp;
        }

        currentVideoTime = this.dateService.addOrSubtractTime(currentVideoTime, secondsPlayed, DateUnit.Sec);

        this.currentAccelerometerData = this.video?.VideoExtensionData?.VideoAccelerometerData?.find(accel => {
            return this.dateService.date1IsAfterDate2(accel.Timestamp, currentVideoTime);
        })

        this.secondsUpdate.emit(secondsPlayed);

        return true;
    }

    play(): void {
        if (!this.vVideoComponents || !this.vVideoComponents.toArray().length) {
            return;
        }

        this.firstPlayStarted = true;

        if (this.ignore) {
            return;
        }

        this.startAnimation();

        this.vVideoComponents.toArray().forEach(video => {
            video.playVideo();
        });

        this.isPlaying = true;
        this.setIgnore();
    }

    pause(): void {
        this.vVideoComponents.toArray().forEach(video => {
            video.pauseVideo();
        });

        this.isPlaying = false;
        this.stopAnimation();
    }

    seek(currentTime: number): void {
        if (this.ignore) {
            return;
        }

        this.vVideoComponents.toArray().forEach(video => {
            video.seek(currentTime);
        });
    }

    setIgnore(): void {
        this.ignore = true;

        setTimeout(() => {
            this.ignore = false;
        }, 10);
    }

    restart(): void {
        this.vVideoComponents.toArray().forEach(video => {
            video.restartVideo();
        });
    }

    mediaLoaded(media: IMedia): void {
        media.loaded = true;
        this.checkStartPlay();
    }

    checkPauseStates(): void {
        let allPaused = true;
        this.vVideoComponents.toArray().forEach(video => {
            if (video.isPlaying()) {
                allPaused = false;
            }
        });

        if (allPaused) {
            this.isPlaying = false;
            this.stopAnimation();
            // to display play/pause icons when source of action is video player, not the controls
            this.temporarilyShowPlayStateIndicator();
        }
    }

    emitIsPlaying(isPlaying: boolean): void {
        this.isPlaying = isPlaying;

        this.videoPlayStateChange.emit(isPlaying);
    }

    // ---

    private initialise(): void {
        this.isMobile = this.constantsService.getIsMobile();
        this.showLargeControls = this.isMobile;
        this.maxVideoDuration = 0;

        // This allows us to ensure we have been created for at least 300ms before we try to play
        setTimeout(() => {
            this.vertical = this.el.nativeElement.offsetWidth / this.el.nativeElement.offsetHeight < 1.4;
        }, 300);
    }

    private stopAnimation(): void {
        // Cancel animation
        cancelAnimationFrame(this.rafNumber);
        this.rafNumber = null;

        // Draw final frame if we are at the end
        this.drawVideoFrames(true);
    }

    private drawVideoFrames(forceUpdate = false): void {

        this.actualAnimationCount++;

        const playTimes: number[] = [];
        this.vVideoComponents.toArray().forEach(video => {
            playTimes.push(video.getCurrentTime());
        });

        if (playTimes.length == 0) {
            return;
        }

        const median = arr => {
            const mid = Math.floor(arr.length / 2);
            const nums = [...arr].sort((a, b) => a - b);

            // This median prefers higher numbers
            return nums[mid];
        };

        const medianPlayTime = median(playTimes)?.toFixed(2);

        if (forceUpdate || this.lastMedianPlayTime != medianPlayTime) {
            this.timeUpdate(medianPlayTime);

            if (forceUpdate || Math.floor(this.lastMedianPlayTime) != Math.floor(medianPlayTime)) {
                this.actualAnimationCount = 0;
            }

            this.lastMedianPlayTime = medianPlayTime;
        }
    }

    private startAnimation(): void {
        this.animate(true);
    }

    private animate(checkExisting: boolean): void {

        if (checkExisting) {
            if (this.rafNumber) {
                this.stopAnimation();
            }
        }

        this.rafNumber = requestAnimationFrame(() => { this.animate(false); });

        if (this.animateCallCount++ % 4 == 0) {
            // We skip every other frame to target 15fps
            return;
        }

        this.drawVideoFrames();
    }

    private checkStartPlay(): void {
        let startPlay = true;

        if (this.video && this.video.channelMedia) {
            this.video.channelMedia.forEach(media => {
                if (!media.loaded) {
                    startPlay = false;
                }
            });
        }

        if (this.firstPlayStarted) {
            this.vVideoComponents?.toArray().forEach(video => {
                if (!video.isPlaying()) {
                    startPlay = false;
                    this.stopAnimation();
                }
            });
        }

        if (startPlay) {
            this.maxVideoDuration = this.getMaxVideoDuration();
            this.play();
        }
    }

    private getMaxVideoDuration(): number {

        let maxDuration = 0;

        this.vVideoComponents?.toArray().forEach(video => {
            maxDuration = Math.max(maxDuration, video.getDuration());
        });

        return maxDuration;
    }

    private getChannelNames(): void {
        if (!this.video.channelMedia?.length) {
            return;
        }

        this.cameraChannelService.getCameraChannels({ VehicleId: this.video.VehicleId }).pipe(untilDestroyed(this)).subscribe(channels => {
            const availableCameraChannels = channels;
            const requestedChannels = this.video.MetaData.ChannelsRequested;

            let unlabelledChannel: IVideoChannelMedia; // The main channel will have no ChannelID so we will save that for last

            this.video.channelMedia.forEach(ch => {
                ch.ChannelLabel = "";
                const cameraChannelIndex = availableCameraChannels.findIndex(cameraChannel => cameraChannel.ChannelId === ch.ChannelId);
                
                if (cameraChannelIndex < 0) {
                    unlabelledChannel = ch;
                    return;
                }

                ch.ChannelLabel = this.translationService.tEnum(CameraLocation, "CameraLocation", availableCameraChannels[cameraChannelIndex].CameraLocation);

                this.addChannelTypeToLabel(availableCameraChannels[cameraChannelIndex], ch);

                availableCameraChannels.splice(cameraChannelIndex, 1);
                requestedChannels.filter(channelId => channelId != availableCameraChannels[cameraChannelIndex].ChannelId);
            })

            if (!availableCameraChannels.length) {
                return;
            }

            const remainingChannel = availableCameraChannels.find(ch => ch.ChannelId == requestedChannels[0]);

            unlabelledChannel.ChannelLabel = this.translationService.tEnum(CameraLocation, "CameraLocation", remainingChannel.CameraLocation);

            this.addChannelTypeToLabel(remainingChannel, unlabelledChannel);
        })
    }

    private addChannelTypeToLabel(channel: ICameraChannel, channelMedia: IVideoChannelMedia): void {
        if (channel.CameraChannelType === CameraChannelType.Standard) {
            return;
        }

        channelMedia.ChannelLabel += ` (${this.translationService.tEnum(CameraChannelType, "CameraChannelType", channel.CameraChannelType)})`
    }
}
